import { useCallback, useState } from "react";
import { CadendarConsts, Days, HorairesDTO, HMObject } from "cadendar-shared";
import { produce } from "immer";
import { DateTime } from "luxon";
import _ from "underts";
import { formaters } from "vincent-utils";
import HorairesPanel, {
  HorairesPanelContent,
  HorairesPanelProps,
} from "../../horaires/components/HorairesPanel";
import Button from "../../main/components/Button.tsx";
import { trpc } from "../../main/components/trpc.ts";
import { useInitialized } from "../../../utils/hooks.ts";

const { days } = CadendarConsts;

const panelContent: HorairesPanelContent = {
  currentTitle: "Planning actuel",
  currentDescription:
    "Consultez l'organisation actuellement appliquée à cette assistante.",
  planningTitle: "Ajouter un nouveau planning",
  planningDescription:
    "Les nouveaux plannings prennent effet à la date de début choisie.",
  draftDescription:
    "Préparez ici les horaires qui s'appliqueront à cette assistante à partir de la date choisie.",
  createButtonTitle: "Créer de nouveaux horaires",
  validationErrorMessage:
    "Corrigez les horaires en erreur avant d'enregistrer.",
  saveHintMessage:
    "Les horaires de l'assistante seront appliqués à partir de la date sélectionnée.",
  displayedDateLabel: "À partir du",
  editDateLabel: "À partir du",
};

const getDefaultLocalHoraire = () =>
  days.reduce(
    (result, jour) => ({
      ...result,
      [jour]: [],
    }),
    {} as HorairesPanelProps["localHoraire"]
  );

const isValidHoraireString = (hstring: string) =>
  !!formaters.getHeureMinuteObjectFromHoraireString(hstring);

const plageInputValidator = (plage: { start: string; end: string }) => {
  const result: { start: string[]; end: string[] } = { start: [], end: [] };
  if (!isValidHoraireString(plage.start)) {
    result.start.push("valeur incorrecte");
  }
  if (!isValidHoraireString(plage.end)) {
    result.end.push("valeur incorrecte");
  }
  if (
    isValidHoraireString(plage.start) &&
    isValidHoraireString(plage.end) &&
    new HMObject(plage.start).minus(new HMObject(plage.end)) > 0
  ) {
    result.start.push("la fin de plage doit être postérieure au début");
    result.end.push("la fin de plage doit être postérieure au début");
  }
  return result;
};

const plagesInputValidator = (plages: { start: string; end: string }[]) => {
  const length = plages.length;
  const result = _.range(0, length).map((_) => ({
    start: [] as string[],
    end: [] as string[],
  }));
  for (let i = 0; i < length - 1; i++) {
    if (
      !isValidHoraireString(plages[i].end) ||
      !isValidHoraireString(plages[i + 1].start)
    ) {
      continue;
    }
    if (
      new HMObject(plages[i].end).minus(new HMObject(plages[i + 1].start)) > 0
    ) {
      result[i].end.push("les plages ne doivent pas se confondre");
      result[i + 1].start.push("les plages ne doivent pas se confondre");
    }
  }
  return result;
};

const validateLocalState = (localState: HorairesPanelProps["localHoraire"]) => {
  const result1 = Object.entries(localState).reduce(
    (result, [jour, plages]) => ({
      ...result,
      [jour]: plages.map((plage) => plageInputValidator(plage)),
    }),
    {} as { [jour in Days]: ReturnType<typeof plagesInputValidator> }
  );
  const result2 = Object.entries(localState).reduce(
    (result, [jour, plages]) => ({
      ...result,
      [jour]: plagesInputValidator(plages),
    }),
    {} as { [jour in Days]: ReturnType<typeof plagesInputValidator> }
  );
  return Object.entries(result1).reduce(
    (result, [jour, plages]) => ({
      ...result,
      [jour]: plages.map((plage, idx) => ({
        start: [...plage.start, ...result2[jour as Days][idx].start],
        end: [...plage.end, ...result2[jour as Days][idx].end],
      })),
    }),
    {} as { [jour in Days]: ReturnType<typeof plagesInputValidator> }
  );
};

const addDurationToHoraireString = (horaireStr: string, minutes: number) => {
  const hour = formaters.getHeureMinuteObjectFromHoraireString(horaireStr);
  if (!hour) {
    return horaireStr;
  }
  return new HMObject(hour).add(minutes).toString();
};

const addPlageToDay = (day: { start: string; end: string }[]) => {
  if (day.length === 0) {
    return [{ start: "8:00", end: "13:00" }];
  }
  const lastBorne = _.last(day)!;
  return [
    ...day,
    {
      start: addDurationToHoraireString(lastBorne.end, 60),
      end: addDurationToHoraireString(lastBorne.end, 120),
    },
  ];
};

const removePlageFromDay = (
  day: { start: string; end: string }[],
  position: number
) => day.filter((_, idx) => idx !== position);

const removeEmptyArrayFromHoraire = (
  horaire: ReturnType<typeof getDefaultLocalHoraire>
) =>
  Object.entries(horaire).reduce(
    (result, [jour, plages]) => ({
      ...result,
      ...(plages.length > 0 && { [jour]: plages }),
    }),
    {} as typeof horaire
  );

const getHoraireToSave = (
  localHoraire: ReturnType<typeof getDefaultLocalHoraire>,
  date: Date,
  secretaryId: string
) => {
  const cleanedHoraire = removeEmptyArrayFromHoraire(localHoraire);
  const horaire = Object.entries(cleanedHoraire).reduce(
    (result, [jour, plages]) => ({
      ...result,
      [jour]: plages.map((plage) => ({
        start: formaters.getHourMinuteObjectFromHoraireString(plage.start),
        end: formaters.getHourMinuteObjectFromHoraireString(plage.end),
      })),
    }),
    {} as HorairesDTO["horaire"]
  );
  return { horaire, format: 2, date, secretaryId };
};

const getHoraireAsString = (horaire: HorairesDTO["horaire"]) => {
  return Object.entries(horaire).reduce(
    (result, [jour, plages]) => ({
      ...result,
      [jour]: plages!.map((plage) => ({
        start: formaters.formatHourMinuteObjectForDisplay(plage.start),
        end: formaters.formatHourMinuteObjectForDisplay(plage.end),
      })),
    }),
    getDefaultLocalHoraire()
  );
};

const getWeeklyTotalMinutes = (
  localHoraire: ReturnType<typeof getDefaultLocalHoraire>
) => {
  return Object.values(localHoraire).reduce((weekTotal, plages) => {
    const dayTotal = plages.reduce((total, plage) => {
      const start = formaters.getHeureMinuteObjectFromHoraireString(plage.start);
      const end = formaters.getHeureMinuteObjectFromHoraireString(plage.end);
      if (!start || !end) {
        return total;
      }
      return total + new HMObject(end).minus(new HMObject(start));
    }, 0);
    return weekTotal + dayTotal;
  }, 0);
};

const getWeeklyTotalMinutesFromSchedule = (horaire: HorairesDTO["horaire"]) => {
  return Object.values(horaire).reduce((weekTotal, plages) => {
    const dayTotal = (plages || []).reduce((total, plage) => {
      return total + new HMObject(plage.end).minus(new HMObject(plage.start));
    }, 0);
    return weekTotal + dayTotal;
  }, 0);
};

export function AssistantScheduleEditor({
  secretaryId,
  contractStartDate,
  assistantName,
}: {
  secretaryId: string;
  contractStartDate: string | null;
  assistantName: string;
}) {
  const { data: fetchedSchedules } = trpc.assistantSchedule.getAll.useQuery({
    secretaryId,
  });
  const schedules = fetchedSchedules ? _.sortBy(fetchedSchedules, "date") : [];
  const [scheduleIndexToDisplay, setScheduleIndexToDisplay] = useState(1);
  const scheduleDates = schedules.map((schedule) => schedule.date);
  const lastSchedule = schedules ? _.nth(schedules, -1) : null;
  const [localHoraire, setLocalHoraire] = useState(getDefaultLocalHoraire());
  const [isEditing, setIsEditing] = useState(false);

  const onOpenEditing = () => {
    setIsEditing(true);
    setLocalHoraire(
      lastSchedule
        ? getHoraireAsString(lastSchedule.horaire)
        : getDefaultLocalHoraire()
    );
  };

  const onPrevWeekToDisplay = () =>
    setScheduleIndexToDisplay((index) => {
      if (index < schedules.length) {
        return index + 1;
      }
      return index;
    });

  const onNextWeekToDisplay = () =>
    setScheduleIndexToDisplay((index) => {
      if (index > 1) {
        return index - 1;
      }
      return index;
    });

  const earliestDate = lastSchedule
    ? DateTime.fromJSDate(lastSchedule.date).plus({ week: 1 }).toJSDate()
    : null;
  const initialDate = earliestDate || new Date();
  const [currentWeek, setCurrentWeek, resetCurrentWeek] = useInitialized(
    initialDate,
    new Date()
  );

  const setNextWeek = () =>
    setCurrentWeek((date) => {
      let newDate = new Date(date);
      do {
        newDate = DateTime.fromJSDate(newDate).plus({ week: 1 }).toJSDate();
      } while (scheduleDates.find((d) => newDate.getTime() === d.getTime()));
      return newDate;
    });

  const setPrevWeek = () =>
    setCurrentWeek((date) => {
      let newDate = new Date(date);
      do {
        newDate = DateTime.fromJSDate(newDate).minus({ week: 1 }).toJSDate();
      } while (scheduleDates.find((d) => newDate.getTime() === d.getTime()));
      return newDate;
    });

  const currentDateInputValue =
    DateTime.fromJSDate(currentWeek).toISODate() || "";

  const setManualDate = (value: string) => {
    const parsed = DateTime.fromISO(value);
    if (!parsed.isValid) {
      return;
    }
    setCurrentWeek(parsed.startOf("day").toJSDate());
  };

  const useContractStartDate = () => {
    if (!contractStartDate) {
      return;
    }
    setManualDate(contractStartDate);
  };

  const editDateControls = (
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-2 text-sm text-[var(--color-quasi-black)]">
        <span>Date de début</span>
        <input
          type="date"
          className="rounded-[10px] border border-[var(--color-grey)] bg-white px-3 py-2 text-sm text-[var(--color-quasi-black)]"
          value={currentDateInputValue}
          onChange={(event) => setManualDate(event.currentTarget.value)}
        />
      </label>
      <Button
        onClick={useContractStartDate}
        title="Date du contrat"
        style="secondary"
        size="small"
        disabled={!contractStartDate}
      />
    </div>
  );

  const setPlages = (jour: Days, plages: { start: string; end: string }[]) => {
    return setLocalHoraire((horaire) =>
      produce(horaire, (draft) => {
        draft[jour] = plages;
      })
    );
  };

  const addPlage = useCallback(
    (jour: Days) =>
      setLocalHoraire((horaire) => ({
        ...horaire,
        [jour]: addPlageToDay(horaire[jour]),
      })),
    []
  );

  const removePlage = useCallback(
    (jour: Days, position: number) =>
      setLocalHoraire((horaire) => ({
        ...horaire,
        [jour]: removePlageFromDay(horaire[jour], position),
      })),
    []
  );

  const copyDayToNext = useCallback(
    (jour: Days) => {
      const currentIndex = days.indexOf(jour);
      const nextDay = days[currentIndex + 1];
      if (!nextDay) {
        return;
      }
      setLocalHoraire((horaire) => ({
        ...horaire,
        [nextDay]: horaire[jour].map((plage) => ({ ...plage })),
      }));
    },
    []
  );

  const weeklyTotalMinutes = getWeeklyTotalMinutes(localHoraire);
  const weeklyTotalLabel = formaters.convertMinToHMinString(weeklyTotalMinutes);
  const editingFooter = (
    <div className="rounded-xl border border-[#d9d7ff] bg-[#f6f5ff] px-4 py-3 text-md text-[var(--color-darker-blue)]">
      Total hebdomadaire: <strong>{weeklyTotalLabel || "0min"}</strong>
    </div>
  );

  const scheduleToDisplay = _.nth(schedules, -scheduleIndexToDisplay);
  const trpcUtils = trpc.useContext();

  const displayedWeeklyTotalMinutes = scheduleToDisplay
    ? getWeeklyTotalMinutesFromSchedule(scheduleToDisplay.horaire)
    : 0;
  const displayedWeeklyTotalLabel = formaters.convertMinToHMinString(
    displayedWeeklyTotalMinutes
  );

  const deleteMutation = trpc.assistantSchedule.delete.useMutation({
    onSettled: () => {
      trpcUtils.assistantSchedule.getAll.invalidate({ secretaryId });
    },
  });

  const deleteDisplayedSchedule = () => {
    if (!scheduleToDisplay) {
      return;
    }
    const shouldDelete = window.confirm(
      `Etes-vous sur de vouloir effacer le planning de ${assistantName}, cette action n'est pas reversible.`
    );
    if (!shouldDelete) {
      return;
    }
    deleteMutation.mutate({
      secretaryId,
      date: scheduleToDisplay.date,
    });
    setScheduleIndexToDisplay(1);
  };

  const existingFooter = scheduleToDisplay ? (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="rounded-xl border border-[#d9d7ff] bg-[#f6f5ff] px-4 py-3 text-md text-[var(--color-darker-blue)]">
        Total hebdomadaire: <span className="text-blue-700">{displayedWeeklyTotalLabel || "0min"}</span>
      </div>
      <Button
        onClick={deleteDisplayedSchedule}
        title={deleteMutation.isPending ? "Suppression..." : "Supprimer ce planning"}
        style="secondary"
        disabled={deleteMutation.isPending}
      />
    </div>
  ) : null;

  const resetUI = () => {
    setIsEditing(false);
    setLocalHoraire(getDefaultLocalHoraire());
    resetCurrentWeek();
  };

  const saveMutation = trpc.assistantSchedule.save.useMutation({
    onSettled: () => {
      trpcUtils.assistantSchedule.getAll.invalidate({ secretaryId });
      trpcUtils.assistantSchedule.byDate.invalidate();
      resetUI();
    },
  });

  const saveNewHoraires = () => {
    const horaireInDb = getHoraireToSave(localHoraire, currentWeek, secretaryId);
    saveMutation.mutate(horaireInDb);
  };

  return (
    <HorairesPanel
      horaire={scheduleToDisplay}
      displayedHoraireDate={scheduleToDisplay?.date}
      onChoosePrevHoraire={onPrevWeekToDisplay}
      onChooseNextHoraire={onNextWeekToDisplay}
      prevHoraireEnabled={scheduleIndexToDisplay < schedules.length}
      nextHoraireEnabled={scheduleIndexToDisplay > 1}
      localHoraire={localHoraire}
      setPrevEditWeek={setPrevWeek}
      setNextEditWeek={setNextWeek}
      addPlage={addPlage}
      removePlage={removePlage}
      setPlages={setPlages}
      copyDayToNext={copyDayToNext}
      isEditing={isEditing}
      setIsEditing={onOpenEditing}
      currentWeek={currentWeek}
      saveNewHoraires={saveNewHoraires}
      isSaving={saveMutation.isPending}
      validationMap={validateLocalState(localHoraire)}
      onCancel={resetUI}
      content={panelContent}
      editDateControls={editDateControls}
      existingFooter={existingFooter}
      editingFooter={editingFooter}
    />
  );
}
